import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPut } from "../lib/api";
import { formatCents } from "../lib/format";

export const Route = createFileRoute("/issues")({ component: AccountingIssuesPage });

type Severity = "blocking" | "decision" | "watch";
type Status = "open" | "in_progress" | "awaiting_validation" | "resolved" | "accepted";

interface Occurrence {
  reference: string;
  date?: string;
  label?: string;
  counterparty?: string | null;
  amountCents?: number;
  accountCode?: string;
  message?: string;
}

interface AccountingIssue {
  id: number;
  type: string;
  severity: Severity;
  status: Status;
  title: string;
  description: string;
  source: string;
  accountCode: string | null;
  proposedAccountCode: string | null;
  periodStart: string;
  periodEnd: string;
  occurrenceCount: number;
  amountCents: number;
  evidence: { occurrences?: Occurrence[]; truncated?: boolean };
  active: boolean;
  resolutionScope: "one_off" | "durable" | null;
  resolutionNote: string | null;
  resolvedBy: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

interface IssuesResponse {
  year: number;
  issues: AccountingIssue[];
  summary: { unresolved: number; open: number; inProgress: number; awaitingValidation: number; resolved: number; blocking: number };
}

const statusLabel: Record<Status, string> = {
  open: "À discuter",
  in_progress: "Correction en cours",
  awaiting_validation: "À valider",
  resolved: "Résolu",
  accepted: "Accepté en l'état",
};
const severityLabel: Record<Severity, string> = { blocking: "Bloquant", decision: "À décider", watch: "À surveiller" };
const severityClass: Record<Severity, string> = {
  blocking: "bg-red-100 text-red-800",
  decision: "bg-amber-100 text-amber-800",
  watch: "bg-blue-100 text-blue-800",
};
const statusClass: Record<Status, string> = {
  open: "bg-orange-100 text-orange-800",
  in_progress: "bg-indigo-100 text-indigo-800",
  awaiting_validation: "bg-purple-100 text-purple-800",
  resolved: "bg-green-100 text-green-800",
  accepted: "bg-gray-100 text-gray-700",
};

function AccountingIssuesPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [showResolved, setShowResolved] = useState(false);
  const [severity, setSeverity] = useState<"all" | Severity>("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const { data, isPending, isError, error } = useQuery<IssuesResponse>({
    queryKey: ["accounting-issues", year],
    queryFn: () => apiGet(`/api/accounting-issues?year=${year}`),
  });

  const issues = useMemo(() => (data?.issues ?? []).filter((issue) => {
    if (!showResolved && (issue.status === "resolved" || issue.status === "accepted")) return false;
    if (severity !== "all" && issue.severity !== severity) return false;
    const needle = search.trim().toLowerCase();
    return !needle || `${issue.id} ${issue.title} ${issue.description} ${issue.source} ${issue.accountCode ?? ""} ${issue.proposedAccountCode ?? ""}`.toLowerCase().includes(needle);
  }), [data, showResolved, severity, search]);

  const toggle = (id: number) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">À décider</h1>
          <p className="mt-1 text-sm text-gray-500">La liste exhaustive des points qui empêchent de considérer la comptabilité comme fiabilisée.</p>
        </div>
        <label className="text-sm text-gray-600">
          <span className="block text-xs mb-1">Exercice</span>
          <input type="number" min="2000" max="2100" value={year} onChange={(event) => setYear(Number(event.target.value))} className="w-24 rounded border border-gray-300 px-2 py-1.5" />
        </label>
      </div>

      {data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Metric label="Restant à traiter" value={data.summary.unresolved} tone="orange" />
          <Metric label="Bloquants" value={data.summary.blocking} tone="red" />
          <Metric label="En cours" value={data.summary.inProgress} tone="indigo" />
          <Metric label="À valider" value={data.summary.awaitingValidation} tone="purple" />
          <Metric label="Traités" value={data.summary.resolved} tone="green" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 text-sm">
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Chercher un problème, un compte…" className="min-w-64 grow rounded border border-gray-300 px-3 py-1.5" />
        <select value={severity} onChange={(event) => setSeverity(event.target.value as "all" | Severity)} className="rounded border border-gray-300 px-2 py-1.5">
          <option value="all">Toutes les priorités</option>
          <option value="blocking">Bloquants</option>
          <option value="decision">À décider</option>
          <option value="watch">À surveiller</option>
        </select>
        <label className="flex items-center gap-2 text-gray-600"><input type="checkbox" checked={showResolved} onChange={(event) => setShowResolved(event.target.checked)} />Afficher les traités</label>
        <BulkValidateButton issues={issues.filter(isQuickValidatable)} />
      </div>

      {isPending && <div className="py-12 text-center text-gray-400">Chargement…</div>}
      {isError && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{(error as Error).message}</div>}
      {!isPending && !isError && issues.length === 0 && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center text-green-800">Aucun problème correspondant à ces filtres.</div>
      )}

      <div className="space-y-3">
        {issues.map((issue) => {
          const open = expanded.has(issue.id);
          const occurrences = issue.evidence?.occurrences ?? [];
          return (
            <article key={issue.id} className={`overflow-hidden rounded-lg border bg-white shadow-sm ${issue.active ? "border-gray-200" : "border-purple-200"}`}>
              <div className="flex w-full items-start gap-3 p-4 hover:bg-gray-50">
                <CopyRefButton issue={issue} />
                <button onClick={() => toggle(issue.id)} className="flex min-w-0 grow items-start gap-3 text-left">
                  <span className="min-w-0 grow">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{issue.title}</span>
                      <Badge className={severityClass[issue.severity]}>{severityLabel[issue.severity]}</Badge>
                      <Badge className={statusClass[issue.status]}>{statusLabel[issue.status]}</Badge>
                      {!issue.active && (issue.status === "awaiting_validation" || issue.resolvedBy === "system") && <Badge className="bg-purple-50 text-purple-700">signal disparu</Badge>}
                    </span>
                    <span className="mt-1 block text-sm text-gray-500">{issue.description}</span>
                    <span className="mt-2 block text-xs text-gray-400">{issue.source.toUpperCase()} · {issue.periodStart === issue.periodEnd ? issue.periodStart : `${issue.periodStart} → ${issue.periodEnd}`} · {issue.occurrenceCount} occurrence{issue.occurrenceCount > 1 ? "s" : ""}{issue.amountCents ? ` · ${formatCents(issue.amountCents)}` : ""}</span>
                  </span>
                </button>
                {isQuickValidatable(issue) && <QuickValidateButton issue={issue} />}
                <button onClick={() => toggle(issue.id)} className="text-sm text-gray-400 pt-0.5">{open ? "▼" : "▶"}</button>
              </div>
              {open && (
                <div className="space-y-3 border-t border-gray-100 p-4">
                  {(issue.accountCode || issue.proposedAccountCode) && <div className="text-xs text-gray-500">Compte actuel : <span className="font-mono">{issue.accountCode ?? "—"}</span> · Compte proposé : <span className="font-mono">{issue.proposedAccountCode ?? "—"}</span></div>}
                  <div className="overflow-x-auto rounded border border-gray-100">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-left text-gray-500"><tr><th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Référence</th><th className="px-2 py-1.5">Détail</th><th className="px-2 py-1.5 text-right">Montant</th></tr></thead>
                      <tbody>{occurrences.map((item, index) => <tr key={`${item.reference}-${index}`} className="border-t border-gray-100"><td className="whitespace-nowrap px-2 py-1.5 font-mono text-gray-500">{item.date ?? "—"}</td><td className="max-w-52 truncate px-2 py-1.5 font-mono text-gray-500" title={item.reference}>{item.reference}</td><td className="px-2 py-1.5 text-gray-700">{item.message ?? item.label ?? item.counterparty ?? "—"}</td><td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">{item.amountCents !== undefined ? formatCents(item.amountCents) : "—"}</td></tr>)}</tbody>
                    </table>
                  </div>
                  {issue.evidence?.truncated && <p className="text-xs text-amber-700">Détail limité aux 500 premières occurrences.</p>}
                  {issue.resolutionNote && <div className="rounded bg-green-50 p-3 text-sm text-green-900"><span className="font-medium">Décision {issue.resolutionScope === "durable" ? "durable" : "ponctuelle"} :</span> {issue.resolutionNote}</div>}
                  <IssueWorkflow issue={issue} />
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

// Le #id copie « #id titre » pour désigner le point dans une discussion, sans
// capture d'écran. Hors du bouton de dépliage : un clic ne replie rien. Repli
// sur execCommand quand l'API Clipboard est absente (contexte non sécurisé).
async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  try {
    textarea.select();
    if (!document.execCommand("copy")) throw new Error("copie refusée");
  } finally {
    textarea.remove();
  }
}

function CopyRefButton({ issue }: { issue: AccountingIssue }) {
  // Un objet neuf à chaque copie : le délai repart même si l'état ne change pas.
  const [feedback, setFeedback] = useState<{ state: "copied" | "failed" } | null>(null);
  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), 1500);
    return () => clearTimeout(timer);
  }, [feedback]);
  const state = feedback?.state ?? "idle";
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        copyText(`#${issue.id} ${issue.title}`).then(() => setFeedback({ state: "copied" }), () => setFeedback({ state: "failed" }));
      }}
      title="Copier la référence"
      className="shrink-0 cursor-pointer rounded pt-0.5 font-mono text-xs text-gray-400 hover:text-gray-700 hover:underline"
    >
      {state === "copied" ? <span className="text-green-700">Copié</span> : state === "failed" ? <span className="text-red-700">Échec</span> : `#${issue.id}`}
    </button>
  );
}

// « Premier contrôle » d'un classement automatique, encore ouvert et dont le
// signal est toujours présent : validable en un clic. La décision est durable
// (même contrepartie + même règle → plus de question) et notée comme telle, ce
// que l'API exige pour un statut final. Un point « signal disparu » ou en cours
// de correction passe par le formulaire complet.
const QUICK_NOTE = "Classement automatique validé en un clic";
function isQuickValidatable(issue: AccountingIssue): boolean {
  return issue.type === "classification_review" && issue.status === "open" && issue.active;
}
function quickValidate(issue: AccountingIssue) {
  return apiPut<{ issue: AccountingIssue }>(`/api/accounting-issues/${issue.id}`, { status: "accepted", resolutionScope: "durable", resolutionNote: QUICK_NOTE });
}

function QuickValidateButton({ issue }: { issue: AccountingIssue }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => quickValidate(issue),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["accounting-issues"] }),
  });
  return (
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="shrink-0 rounded bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
      title={`Accepter ce classement (${issue.proposedAccountCode ?? "compte proposé"}) pour cette contrepartie, durablement`}
    >
      {mutation.isPending ? "…" : mutation.isError ? "Échec, réessayer" : "✓ Valider"}
    </button>
  );
}

// Tous les premiers contrôles affichés (filtres compris), l'un après l'autre.
// Une erreur n'arrête pas la série : les échecs sont comptés et listés, les
// points concernés restent affichés pour être retentés.
function BulkValidateButton({ issues }: { issues: AccountingIssue[] }) {
  const queryClient = useQueryClient();
  const [done, setDone] = useState(0);
  const [report, setReport] = useState<{ ok: number; failed: { id: number; message: string }[] } | null>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      setDone(0);
      setReport(null);
      const failed: { id: number; message: string }[] = [];
      let ok = 0;
      for (const issue of issues) {
        try {
          await quickValidate(issue);
          ok++;
        } catch (error) {
          failed.push({ id: issue.id, message: (error as Error).message });
        }
        setDone((n) => n + 1);
      }
      return { ok, failed };
    },
    onSettled: (result) => {
      if (result) setReport(result);
      queryClient.invalidateQueries({ queryKey: ["accounting-issues"] });
    },
  });
  if (issues.length === 0 && !report) return null;
  return (
    <span className="flex items-center gap-2">
      {issues.length > 0 && (
        <button
          onClick={() => {
            if (confirm(`Valider les ${issues.length} premiers contrôles affichés ? Chaque classement automatique sera accepté durablement pour sa contrepartie.`)) mutation.mutate();
          }}
          disabled={mutation.isPending}
          className="rounded border border-green-600 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
        >
          {mutation.isPending ? `Validation… ${done}/${issues.length}` : `✓ Valider les ${issues.length} premiers contrôles affichés`}
        </button>
      )}
      {report && (
        <span className={`text-xs ${report.failed.length ? "text-red-700" : "text-green-700"}`} title={report.failed.map((f) => `#${f.id} : ${f.message}`).join("\n")}>
          {report.ok} validé{report.ok > 1 ? "s" : ""}
          {report.failed.length > 0 && <> · {report.failed.length} en échec ({report.failed.map((f) => `#${f.id}`).join(", ")}), toujours affichés</>}
        </span>
      )}
    </span>
  );
}

function IssueWorkflow({ issue }: { issue: AccountingIssue }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>(issue.status);
  const [resolutionScope, setResolutionScope] = useState<"one_off" | "durable">(issue.resolutionScope ?? "one_off");
  const [resolutionNote, setResolutionNote] = useState(issue.resolutionNote ?? "");
  const finalStatus = status === "resolved" || status === "accepted";

  useEffect(() => {
    setStatus(issue.status);
    setResolutionScope(issue.resolutionScope ?? "one_off");
    setResolutionNote(issue.resolutionNote ?? "");
  }, [issue.status, issue.resolutionScope, issue.resolutionNote]);

  const mutation = useMutation({
    mutationFn: () => apiPut<{ issue: AccountingIssue }>(`/api/accounting-issues/${issue.id}`, {
      status,
      resolutionScope: finalStatus ? resolutionScope : null,
      resolutionNote: finalStatus ? resolutionNote : null,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["accounting-issues"] });
    },
  });

  return (
    <form
      className="space-y-3 rounded border border-gray-200 bg-gray-50 p-3"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs font-medium text-gray-600">
          <span className="mb-1 block">État du point</span>
          <select value={status} onChange={(event) => setStatus(event.target.value as Status)} className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800">
            {(Object.keys(statusLabel) as Status[]).map((value) => <option key={value} value={value}>{statusLabel[value]}</option>)}
          </select>
        </label>
        {finalStatus && (
          <label className="text-xs font-medium text-gray-600">
            <span className="mb-1 block">Portée de la décision</span>
            <select value={resolutionScope} onChange={(event) => setResolutionScope(event.target.value as "one_off" | "durable")} className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800">
              <option value="one_off">Ponctuelle — cette occurrence</option>
              <option value="durable">Durable — mêmes cas futurs</option>
            </select>
          </label>
        )}
      </div>
      {finalStatus && (
        <label className="text-xs font-medium text-gray-600">
          <span className="mb-1 block">Synthèse de la décision</span>
          <textarea
            value={resolutionNote}
            onChange={(event) => setResolutionNote(event.target.value)}
            required
            rows={2}
            placeholder="Conclusion validée ensemble et justification comptable…"
            className="w-full resize-y rounded border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal text-gray-800"
          />
        </label>
      )}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={mutation.isPending || (finalStatus && !resolutionNote.trim())} className="rounded bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50">
          {mutation.isPending ? "Enregistrement…" : "Enregistrer l’état"}
        </button>
        {mutation.isSuccess && <span className="text-xs text-green-700">Décision enregistrée.</span>}
        {mutation.isError && <span className="text-xs text-red-700">{(mutation.error as Error).message}</span>}
      </div>
    </form>
  );
}

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>{children}</span>;
}

function Metric({ label, value, tone }: { label: string; value: number; tone: "orange" | "red" | "indigo" | "purple" | "green" }) {
  const colors = { orange: "border-orange-200 text-orange-800", red: "border-red-200 text-red-800", indigo: "border-indigo-200 text-indigo-800", purple: "border-purple-200 text-purple-800", green: "border-green-200 text-green-800" };
  return <div className={`rounded-lg border bg-white p-3 ${colors[tone]}`}><div className="text-xs opacity-70">{label}</div><div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div></div>;
}
